// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useApp } from '../../store/AppContext.js';
import { ASSET_TYPES, LIABILITY_TYPES } from '../../lib/eventLog.js';
import { getExchangeRate, convertCurrency } from '../../lib/currency-math.js';
import { getCurrencyDecimalPlaces, getCurrencySymbol } from '../../lib/currencies.js';
import { formatDate } from '../../lib/formatLocale.js';
import {
  INVESTMENT_ACCOUNT_TYPES,
  getCommodityId,
  calculateSharesHeld,
  calculateMarketValue,
  calculateCostBasis,
  calculateTotalCostBasis,
  getLatestPrice,
} from '../../lib/investment.js';

export function NetWorthChart() {
  const { t } = useTranslation(['reports', 'common']);
  const { state } = useApp();
  const { ledger } = state;
  const homeCurrency = ledger.settings.homeCurrency;
  const homeDecimals = getCurrencyDecimalPlaces(homeCurrency);
  const homeSymbol = getCurrencySymbol(homeCurrency);
  const homeDivisor = Math.pow(10, homeDecimals);
  const [missingPriceWarning, setMissingPriceWarning] = useState(false);

  const allPrices = useMemo(() => [...ledger.prices.values()], [ledger.prices]);

  const chartData = useMemo(() => {
    const now = new Date();
    const months: string[] = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(d.toISOString().slice(0, 7));
    }

    const transactions = [...ledger.transactions.values()]
      .filter((t) => !t.voided)
      .sort((a, b) => a.date.localeCompare(b.date));

    let anyMissingPrice = false;

    const data = months.map((ym) => {
      const endOfMonth = ym + '-31';
      let assets = 0;
      let liabilities = 0;

      for (const account of ledger.accounts.values()) {
        if (account.placeholder) continue;

        const isInvestment = INVESTMENT_ACCOUNT_TYPES.has(account.type);

        if (isInvestment && ASSET_TYPES.has(account.type)) {
          // Use market value for investment accounts
          const accountTxs = transactions.filter((t) =>
            t.date <= endOfMonth && t.splits.some((s) => s.accountId === account.id),
          );
          const shares = calculateSharesHeld(account.id, accountTxs, endOfMonth);
          const commodityId = getCommodityId(account);
          const latestPrice = getLatestPrice(commodityId, endOfMonth, allPrices);

          if (latestPrice) {
            assets += calculateMarketValue(shares, latestPrice.value);
          } else {
            // Fallback to cost basis
            const lots = calculateCostBasis(account.id, accountTxs);
            assets += calculateTotalCostBasis(lots);
            if (shares > 0) anyMissingPrice = true;
          }
        } else {
          let balance = account.balance;
          for (const tx of transactions) {
            if (tx.date > endOfMonth) {
              for (const split of tx.splits) {
                if (split.accountId === account.id) {
                  balance -= split.amount;
                }
              }
            }
          }
          let homeBalance = balance;
          if (account.currency !== homeCurrency) {
            const rate = getExchangeRate(account.currency, homeCurrency, endOfMonth, allPrices);
            homeBalance = rate !== null
              ? convertCurrency(balance, account.currency, homeCurrency, rate, getCurrencyDecimalPlaces(account.currency), homeDecimals)
              : 0;
          }
          if (ASSET_TYPES.has(account.type)) assets += homeBalance;
          else if (LIABILITY_TYPES.has(account.type)) liabilities += homeBalance;
        }
      }

      return {
        month: ym,
        label: formatDate(ym + '-15', { month: 'short', year: '2-digit' }),
        assets: assets / homeDivisor,
        liabilities: Math.abs(liabilities) / homeDivisor,
        netWorth: (assets + liabilities) / homeDivisor,
      };
    });

    setMissingPriceWarning(anyMissingPrice);
    return data;
  }, [ledger.accounts, ledger.transactions, allPrices]);

  function formatDollar(value: number) {
    return `${homeSymbol}${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }

  const currentNetWorth = chartData[chartData.length - 1]?.netWorth ?? 0;

  return (
    <div className="flex-1 overflow-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-md font-semibold text-text">{t('reports:titles.netWorth')}</h2>
        <div className="text-right">
          <p className="text-xs text-text-muted">{t('reports:titles.currentNetWorth')}</p>
          <p className={`text-xl font-bold font-mono ${currentNetWorth >= 0 ? 'text-positive' : 'text-negative'}`}>
            {formatDollar(currentNetWorth)}
          </p>
        </div>
      </div>

      <div className="bg-bg-surface border border-border p-4">
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={chartData} margin={{ top: 8, right: 24, left: 24, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2A2D3A" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#7B7F9E' }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={formatDollar} tick={{ fontSize: 11, fill: '#7B7F9E' }} width={80} axisLine={false} tickLine={false} />
            <Tooltip
              contentStyle={{ background: '#222535', border: '1px solid #2A2D3A', borderRadius: 0 }}
              labelStyle={{ color: '#E8E9F0', fontWeight: 600 }}
              itemStyle={{ color: '#7B7F9E' }}
              formatter={(v) => [formatDollar(Number(v))]}
            />
            <Legend wrapperStyle={{ fontSize: 12, color: '#7B7F9E' }} />
            <Line type="monotone" dataKey="assets" stroke="#3D9970" strokeWidth={2} dot={false} name={t('reports:sections.assets')} isAnimationActive={false} />
            <Line type="monotone" dataKey="liabilities" stroke="#C0392B" strokeWidth={2} dot={false} name={t('reports:sections.liabilities')} isAnimationActive={false} />
            <Line type="monotone" dataKey="netWorth" stroke="#4A7FA5" strokeWidth={2.5} dot={false} name={t('reports:sections.netWorth')} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <p className="text-xs text-text-subtle mt-2 text-center">
        {t('reports:netWorth.footnote')}
      </p>
      {missingPriceWarning && (
        <p className="text-xs text-warning mt-1 text-center">
          {t('reports:netWorth.missingPriceWarning')}
        </p>
      )}
    </div>
  );
}
